import React, { useState, useCallback } from 'react';
import { Typography, List, Tag, Space, Card, Row, Col, Flex, Switch, Form, Input, Button, theme, message } from 'antd';
import ResponsiveModal from '../../../Shared/ResponsiveModal/ResponsiveModal';
import { HighlightOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { beautifyEntry } from '../../KnowledgeDatabaseServices';
import { useTranslation } from 'react-i18next';
import { ICON_SIZE } from '../../../Shared/Styles/iconButton';
import { formatShortDate } from '../../../../Utils/date';
import BeautifyControl from '../../../Shared/BeautifyControl/BeautifyControl';

const { Text } = Typography;

const EntryItem = ({ item, onDelete, onToggle, onUpdate, updating, knowledgeBaseId, isActive = false, onActivate, onDeactivate, className }) => {
    const { t, i18n } = useTranslation();
    const { token } = theme.useToken();
    const [isHovered, setIsHovered] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [form] = Form.useForm();

    const buildBeautifyPayload = useCallback((type, value) => {
        if (type === 'qa-both') {
            const question = value?.question ?? '';
            const answer = value?.answer ?? '';
            return {
                entry_type: 'question_answer',
                question,
                answer,
            };
        }
        // business
        return {
            entry_type: 'business_info',
            content: value,
        };
    }, []);

    const createRunBeautify = useCallback((kind) => {
        return async (text) => {
            if (!knowledgeBaseId) {
                message.error(t('no_database_id_provided'));
                return { suggestions: [] };
            }

            const values = form.getFieldsValue();
            const payload = buildBeautifyPayload(
                kind,
                kind === 'qa-both'
                    ? { question: values.question, answer: values.answer }
                    : text
            );

            try {
                const data = await beautifyEntry(knowledgeBaseId, payload);
                const rawWarnings = data?.data?.warnings || [];
                const warnings = Array.isArray(rawWarnings) ? rawWarnings : [];

                const rawSuggestions = data?.data?.suggestions || data?.suggestions || [];
                let suggestionsList;

                if (kind === 'business') {
                    suggestionsList = rawSuggestions.map(s => s?.content ?? s);
                } else if (kind === 'qa-both') {
                    suggestionsList = rawSuggestions.map(s => ({
                        question: s?.question ?? '',
                        answer: s?.answer ?? '',
                    }));
                } else {
                    suggestionsList = rawSuggestions.map(s => s?.content ?? s);
                }

                if (!suggestionsList.length) {
                    message.info(t('no_suggestions_available'));
                    return { suggestions: [] };
                }
                const coinsAmount = Math.abs(data?.coins_amount ?? data?.data?.coins_amount ?? 0);
                const analysisText = data?.analysis ?? data?.data?.analysis ?? '';

                return {
                    suggestions: suggestionsList,
                    coinsAmount,
                    analysis: analysisText,
                    warnings,
                };
            } catch (error) {
                message.error(error?.response?.data?.message || t('something_went_wrong'));
                return { suggestions: [] };
            }
        };
    }, [buildBeautifyPayload, form, knowledgeBaseId, t]);

    const cardStyle = {
        marginBottom: 8,
        boxShadow: (isHovered || isActive) ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 1px 4px rgba(0, 0, 0, 0.05)',
        transition: 'all 0.3s',
        borderRadius: 4,
        width: '100%'
    };

    const contentBoxStyle = {
        padding: '0',
        borderRadius: 4,
    };

    const questionBoxStyle = {
        ...contentBoxStyle,
        backgroundColor: token.colorInfoBg,
        border: `1px solid ${token.colorBorderSecondary}`
    };

    const answerBoxStyle = {
        ...contentBoxStyle,
        backgroundColor: token.colorFillSecondary,
        border: `1px solid ${token.colorBorder}`
    };

    const contentOpacityStyle = item.is_disabled ? { opacity: 0.6 } : {};

    const showActions = (isHovered || isActive) && !isEditing;

    const activateActions = (event) => {
        if (isEditing) return;

        const interactiveElement = event?.target?.closest?.('button, input, textarea, select, a, [role="button"], .ant-switch');
        if (interactiveElement) return;

        if (onActivate) {
            onActivate();
        } else {
            setIsHovered(true);
        }
    };

    const startEditing = (event) => {
        event?.stopPropagation?.();
        setIsEditing(true);
        setIsHovered(false);
        onActivate?.();
        // initialize form with current item values
        if (item.entry_type === 'question_answer') {
            form.setFieldsValue({ question: item.data.question, answer: item.data.answer });
        } else {
            form.setFieldsValue({ content: item.data.content });
        }
    };

    const actions = showActions ? [
        <Space key="entry-actions">
            <EditOutlined
                key="edit"
                style={{ fontSize: 16, color: '#1890ff' }}
                onClick={startEditing}
            />
            <DeleteOutlined
                key="delete"
                style={{ fontSize: 16, color: '#ff4d4f' }}
                onClick={(event) => {
                    event?.stopPropagation?.();
                    setDeleteModalVisible(true);
                }}
            />
        </Space>
    ] : [];

    const handleCancel = (event) => {
        event?.stopPropagation?.();
        form.resetFields();
        setIsEditing(false);
        setIsHovered(false);
        onDeactivate?.();
    };

    const handleSave = async (event) => {
        event?.stopPropagation?.();
        try {
            const values = await form.validateFields();
            if (onUpdate) {
                await onUpdate(item, values);
            }
            setIsEditing(false);
            setIsHovered(false);
            onDeactivate?.();
        } catch (e) {
            // validation errors are handled by antd
        }
    };

    const confirmDelete = async () => {
        try {
            setIsDeleting(true);
            await onDelete(item.id);
        } finally {
            setIsDeleting(false);
            setDeleteModalVisible(false);
        }
    };

    return (
        <List.Item style={{ padding: 0, width: '100%' }}>
            <Card
                style={cardStyle}
                className={className}
                styles={{ body: { padding: '12px' } }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onPointerDown={activateActions}
            >
                <Row>
                    <Col span={24}>
                        <Flex style={{ width: '100%', marginBottom: 8 }} align="flex-start" gap={8}>
                            <Space direction="vertical" size={2} style={{ flexGrow: 1, minWidth: 0 }}>
                                <Tag color={item.entry_type === 'question_answer' ? 'blue' : 'green'}
                                    style={{ padding: '2px 8px', fontSize: 12, margin: 0, width: 'fit-content' }}>
                                    {item.entry_type === 'question_answer' ? t('qa') : t('business_info')}
                                </Tag>
                                <Text
                                    type="secondary"
                                    style={{ fontSize: 12, lineHeight: 1.35 }}
                                >
                                    {item.id ? `#${item.id}` : ''}
                                </Text>
                                <Text type="secondary" style={{ fontSize: 12, padding: 0, lineHeight: 1.35 }}>
                                    {formatShortDate(item.created_at, i18n.language)}
                                </Text>
                            </Space>
                            <Flex align="center" gap={8} style={{ flexShrink: 0 }}>
                                <Switch
                                    checked={!item.is_disabled}
                                    onChange={(checked) => onToggle && onToggle(item, !checked)}
                                    size="small"
                                />
                                {actions}
                            </Flex>
                        </Flex>

                        <div style={contentOpacityStyle}>
                            {isEditing ? (
                                <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
                                    {item.entry_type === 'question_answer' ? (
                                        <Space direction="vertical" style={{ width: '100%' }} size={0}>
                                            <Form.Item
                                                name="question"
                                                label={t('question')}
                                                rules={[{ required: true, message: t('please_enter_question') }]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Input.TextArea autoSize={{ minRows: 2 }} placeholder={t('enter_question_here')} dir="auto" style={{ unicodeBidi: 'plaintext' }} />
                                            </Form.Item>
                                            <Form.Item
                                                name="answer"
                                                style={{ marginBottom: 0 }}
                                                label={t('answer')}
                                                rules={[{ required: true, message: t('please_enter_answer') }]}
                                            >
                                                <Input.TextArea autoSize={{ minRows: 3 }} placeholder={t('enter_answer_here')} dir="auto" style={{ unicodeBidi: 'plaintext' }} />
                                            </Form.Item>
                                            <Form.Item
                                                noStyle
                                                shouldUpdate={(prev, current) =>
                                                    prev.question !== current.question ||
                                                    prev.answer !== current.answer
                                                }
                                            >
                                                {({ getFieldValue }) => {
                                                    const question = (getFieldValue('question') || '').trim();
                                                    const answer = (getFieldValue('answer') || '').trim();
                                                    const beautifyDisabled = !question || !answer;

                                                    return (
                                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8, marginBottom: 0 }}>
                                                            <BeautifyControl
                                                                key="qa-both-beautify"
                                                                value={{
                                                                    question: getFieldValue('question'),
                                                                    answer: getFieldValue('answer'),
                                                                }}
                                                                onChange={(next) => {
                                                                    if (!next) return;
                                                                    const currentValues = form.getFieldsValue();
                                                                    form.setFieldsValue({
                                                                        question: next.question ?? currentValues.question,
                                                                        answer: next.answer ?? currentValues.answer,
                                                                    });
                                                                }}
                                                                runBeautify={createRunBeautify('qa-both')}
                                                                beautifyLabel={t('beautify')}
                                                                beautifyIcon={<HighlightOutlined style={{ fontSize: ICON_SIZE }} />}
                                                                revertLabel={t('revert')}
                                                                disabled={beautifyDisabled}
                                                                buttonStyle={{ color: '#7F56D9' }}
                                                            />
                                                        </div>
                                                    );
                                                }}
                                            </Form.Item>
                                        </Space>
                                    ) : (
                                        <>
                                            <Form.Item
                                                name="content"
                                                style={{ marginBottom: 0 }}
                                                label={t('business_information')}
                                                rules={[{ required: true, message: t('please_enter_business_information') }]}
                                            >
                                                <Input.TextArea autoSize={{ minRows: 4 }} placeholder={t('enter_business_information_here')} dir="auto" style={{ unicodeBidi: 'plaintext' }} />
                                            </Form.Item>
                                            <Form.Item
                                                noStyle
                                                shouldUpdate={(prev, current) =>
                                                    prev.content !== current.content
                                                }
                                            >
                                                {({ getFieldValue }) => {
                                                    const content = (getFieldValue('content') || '').trim();
                                                    const beautifyDisabled = !content;

                                                    return (
                                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4, marginBottom: 4 }}>
                                                            <BeautifyControl
                                                                key="business-content-beautify"
                                                                value={getFieldValue('content')}
                                                                onChange={(text) => form.setFieldsValue({ content: text })}
                                                                runBeautify={createRunBeautify('business')}
                                                                beautifyLabel={t('beautify')}
                                                                beautifyIcon={<HighlightOutlined style={{ fontSize: ICON_SIZE }} />}
                                                                revertLabel={t('revert')}
                                                                disabled={beautifyDisabled}
                                                                buttonStyle={{ color: '#7F56D9' }}
                                                            />
                                                        </div>
                                                    );
                                                }}
                                            </Form.Item>
                                        </>
                                    )}
                                    <Flex gap={8} wrap style={{ marginTop: 8 }}>
                                        <Button onClick={handleCancel} block>
                                            {t('cancel')}
                                        </Button>
                                        <Button type="primary" onClick={handleSave} loading={updating} block>
                                            {t('update_entry')}
                                        </Button>
                                    </Flex>
                                </Form>
                            ) : (
                                <>
                                    {item.entry_type === 'question_answer' ? (
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            <Card style={questionBoxStyle} styles={{ body: { padding: '12px' } }} bordered={false}>
                                                <Text strong style={{ fontSize: 13, color: token.colorLink, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                    {t('q_prefix')} {item.data.question}
                                                </Text>
                                            </Card>
                                            <Card style={answerBoxStyle} styles={{ body: { padding: '12px' } }} bordered={false}>
                                                <Text style={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                    {t('a_prefix')} {item.data.answer}
                                                </Text>
                                            </Card>
                                        </Space>
                                    ) : (
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            <Card styles={{ body: { padding: '12px' } }} style={answerBoxStyle} bordered={false}>
                                                <Text style={{ fontSize: 13, padding: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                    {item.data.content}
                                                </Text>
                                            </Card>
                                        </Space>
                                    )}
                                </>
                            )}
                        </div>
                    </Col>
                </Row>
            </Card>
            <ResponsiveModal
                title={t('delete_this_entry')}
                open={deleteModalVisible}
                onOk={confirmDelete}
                onCancel={() => setDeleteModalVisible(false)}
                okText={t('yes')}
                cancelText={t('no')}
                okButtonProps={{ danger: true }}
                confirmLoading={isDeleting}
                desktopWidth={420}
            >
                <div>
                    {item.entry_type === 'question_answer' ? (
                        <>
                            <div style={{ marginTop: 8, opacity: 0.8 }}>
                                <Text type="secondary">{t('q_prefix')} {item.data?.question}</Text>
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={{ marginTop: 8, opacity: 0.8 }}>
                                <Text type="secondary">{(item.data?.content || '').slice(0, 180)}{(item.data?.content || '').length > 80 ? '…' : ''}</Text>
                            </div>
                        </>
                    )}
                </div>
            </ResponsiveModal>
        </List.Item >
    );
};

export default EntryItem;
